import React, { useState, useContext, useEffect } from 'react';
import settings from '../settings.js';
import { Container, TextField, Button, Typography, Box, Alert } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { AuthenticationContext } from '../contexts/AuthenticationContext';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { handleLogin as handleLoginUtil } from '../utils/auth';

const url = settings.getUrl();

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [csrfToken, setCsrfToken] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { setUser } = useContext(AuthenticationContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    const fetchCsrfToken = async () => {
      try {
        const response = await fetch(`${url}/auth/csrf-token`, {
          method: 'GET',
          credentials: 'include',
        });
        if (!response.ok) {
          throw new Error(`CSRF token fetch failed: ${response.statusText}`);
        }
        const data = await response.json();
        setCsrfToken(data.csrf_token);
      } catch (error) {
        console.error('Error fetching CSRF token:', error);
        setError('Failed to initialize login. Please refresh the page.');
      }
    };

    fetchCsrfToken();
  }, []);

  const handleLogin = async (event) => {
    if (event) {
      event.preventDefault();
    }
    await handleLoginUtil({ email, password, csrfToken, setUser, navigate, setError });
  };

  return (
    <Container
      maxWidth={isMobile ? false : "sm"}
      disableGutters={isMobile}
      sx={{
        pt: isMobile ? 0 : 5,
        pb: isMobile ? 0 : 5,
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        bgcolor: isMobile ? 'background.default' : 'transparent',
      }}
    >
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="flex-start"
        mt={isMobile ? 4 : 8}
        p={isMobile ? 2 : 3}
        boxShadow={isMobile ? 0 : 3}
        borderRadius={isMobile ? 0 : 2}
        sx={{
          width: isMobile ? '100vw' : '100%',
          maxWidth: isMobile ? '100vw' : 400,
          bgcolor: isMobile ? 'background.default' : 'background.paper',
        }}
      >
        <Typography
          variant={isMobile ? "h5" : "h4"}
          gutterBottom
          sx={{
            mt: isMobile ? 2 : 0,
            mb: isMobile ? 2 : 3,
            fontWeight: 600,
            textAlign: 'center',
          }}
        >
          Login
        </Typography>
        {error && (
          <Alert severity="error" sx={{ width: '100%', mb: 2, fontSize: isMobile ? '1rem' : undefined }}>
            {error}
          </Alert>
        )}
        <Box
          component="form"
          onSubmit={handleLogin}
          sx={{
            width: '100%',
            maxWidth: isMobile ? '100vw' : 400,
            px: isMobile ? 2 : 0,
          }}
        >
          <TextField
            id="email"
            label="Email Address"
            variant="outlined"
            margin="normal"
            fullWidth
            name="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoFocus
          />
          <TextField
            id="password"
            label="Password"
            type="password"
            variant="outlined"
            margin="normal"
            fullWidth
            name="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            sx={{
              mt: 3,
              mb: 2,
              py: isMobile ? 1.5 : 1,
              fontSize: isMobile ? '1.1rem' : undefined,
              borderRadius: isMobile ? 0.5 : 2,
            }}
          >
            Login
          </Button>
        </Box>
      </Box>
    </Container>
  );
};

export default LoginPage;
